import React, { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../assets/logo3.png";
import { Popover, Transition } from "@headlessui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PeopleIcon from "@mui/icons-material/People";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { IoPersonAdd } from "react-icons/io5";
import { FaRegUser } from "react-icons/fa";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { IoCalendarNumberOutline } from "react-icons/io5";

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 65;

const THEME = {
  primary:      "#2F6DB4",
  primaryLight: "#5894d9",
  bg:           "#E3ECF6",
  border:       "#BBD6F5",
  glow:         "rgba(47,109,180,0.35)",
};

const navItems = [
  {
    label:       "Patients",
    icon:        PeopleIcon,
    path:        "/receptionist",
    activePaths: ["/receptionist", "/receptionist/add"],
  },
  {
    label:       "Checkup",
    icon:        MedicalServicesIcon,
    path:        "/checkup",
    activePaths: ["/checkup", "/checkup/add"],
  },
  {
    label:       "Admission",
    icon:        IoPersonAdd,
    path:        "/admission",
    activePaths: ["/admission", "/admission/add"],
  },
   {
    label:       "Appointment",
    icon:        IoCalendarNumberOutline,
    path:        "/appointment",
    activePaths: ["/appointment", "/appointment/add"],
  },
];


const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH : DRAWER_COLLAPSED,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
  "& .MuiDrawer-paper": {
    width: open ? DRAWER_WIDTH : DRAWER_COLLAPSED,
    border: "none",
    borderTopRightRadius: 24,
    borderBottomRightRadius: 24,
    background: "#ffffff",
    boxShadow: "6px 0 32px rgba(0,0,0,0.10)",
    overflow: "hidden",
    zIndex: 40,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}));


export default function PortalLayout({ children }) {
  const [sidebarOpen, setSidebarOpen]     = useState(true);  
  const [mobileOpen, setMobileOpen]       = useState(false);  
  const [isMobile, setIsMobile]           = useState(false);
  const [isTablet, setIsTablet]           = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [loggedUser, setLoggedUser]       = useState({ id: "", name: "" });

  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setIsMobile(w < 640);
      setIsTablet(w >= 640 && w < 1024);
  
      if (w >= 640 && w < 1024) setSidebarOpen(false);
      if (w >= 1024) setSidebarOpen(true);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);


  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const id   = localStorage.getItem("user_id");
    const name = localStorage.getItem("user_name");
    if (id && name) setLoggedUser({ id, name });
  }, []);

  const handleMenuClick = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
    else setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    Cookies.remove("XIOQUNVU1RPTUVSLUFVVEhFTlRJQ0FUSU9OIMSLQ1JFVC1LRVk=");
    toast.success("Logout Successfully");
    navigate("/login", { replace: true });
  };

  if (location.pathname === "/login") return children;

  const pageTitle = location.pathname.startsWith("/receptionist")
    ? "Patients"
    : location.pathname.startsWith("/checkup")
    ? "Checkup"
    : location.pathname.startsWith("/admission")
    ? "Admission"
    : "Appointment";


  const desktopSidebarW = isMobile ? 0 : sidebarOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED;


  const SidebarContent = ({ expanded }) => (
    <div className="flex flex-col h-full">
     
      <div className="flex items-center justify-center px-3 pt-5 pb-4 border-b border-gray-100 mb-2">
        <img
          src={logo}
          alt="Hospital Logo"
          className="object-contain transition-all duration-300"
          style={{ width: expanded ? 192 : 40, height: expanded ? 192 : 40 }}
        />
      </div>

    
      {expanded && (
        <p className="text-[10px] font-bold tracking-[1.2px] uppercase text-gray-400 px-5 pt-2 pb-1 m-0">
          Navigation
        </p>
      )}

      
      <nav className="py-1 flex-1">
        {navItems.map((item) => {
          const isActive = item.activePaths.includes(location.pathname);
          const Icon     = item.icon;
          return (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              title={!expanded ? item.label : ""}
              className="relative flex items-center cursor-pointer rounded-xl overflow-hidden mx-2 my-[2px] px-[10px] py-[9px] border transition-all duration-200"
              style={
                isActive
                  ? { background: THEME.bg, borderColor: THEME.border, boxShadow: `0 2px 8px ${THEME.primary}22` }
                  : { background: "transparent", borderColor: "transparent" }
              }
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background  = "#f8f9fa";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background  = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }
              }}
            >
              
              <div
                className="flex items-center justify-center rounded-[8px] flex-shrink-0 transition-all duration-200"
                style={{
                  width: 32, height: 32,
                  background: isActive ? THEME.primary : "#f1f3f5",
                  boxShadow:  isActive ? `0 4px 14px ${THEME.glow}` : "none",
                  border:     isActive ? "none" : "1px solid #e5e7eb",
                }}
              >
                <Icon
                  sx={{ fontSize: 17 }}
                  style={{ fontSize: 17, color: isActive ? "#fff" : "#6b7280" }}
                />
              </div>

             
              {expanded && (
                <span
                  className="ml-[10px] text-[13px] font-semibold whitespace-nowrap overflow-hidden flex-1"
                  style={{ color: isActive ? THEME.primary : "#4b5563" }}
                >
                  {item.label}
                </span>
              )}

              
              {isActive && (
                <div
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-[55%] rounded-l"
                  style={{ background: THEME.primary }}
                />
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="flex items-stretch min-h-screen bg-slate-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        * { font-family: 'Plus Jakarta Sans', sans-serif; }
      `}</style>

      <CssBaseline />

     
      {isMobile && (
        <>
          
          <div
            className="fixed inset-0 z-50 transition-opacity duration-300"
            style={{
              background:     "rgba(0,0,0,0.45)",
              opacity:         mobileOpen ? 1 : 0,
              pointerEvents:   mobileOpen ? "auto" : "none",
              backdropFilter:  mobileOpen ? "blur(2px)" : "none",
            }}
            onClick={() => setMobileOpen(false)}
          />

          
          <div
            className="fixed top-0 left-0 h-full z-[60] bg-white shadow-2xl"
            style={{
              width:      DRAWER_WIDTH,
              borderTopRightRadius:    24,
              borderBottomRightRadius: 24,
              transform:  mobileOpen ? "translateX(0)" : `translateX(-${DRAWER_WIDTH}px)`,
              transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
            >
              <CloseIcon style={{ fontSize: 18, color: "#6b7280" }} />
            </button>
            <SidebarContent expanded={true} />
          </div>
        </>
      )}

      
      {!isMobile && (
        <DesktopDrawer variant="permanent" open={sidebarOpen}>
          <SidebarContent expanded={sidebarOpen} />
        </DesktopDrawer>
      )}

      
      <div
        className="flex flex-col flex-1 min-w-0 transition-all duration-300"
        style={{ minHeight: "100vh" }}
      >
        
        <div className="sticky top-0 z-30 px-2 pt-2 pb-0">
          <div className="rounded-2xl border border-white/70 bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
            <div className="flex items-center min-h-[56px] px-3 gap-2">

            
              <button
                onClick={handleMenuClick}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-700/[0.07] hover:bg-gray-700/[0.14] transition-colors flex-shrink-0"
              >
                <MenuIcon className="!text-[20px] !text-gray-700" />
              </button>

             
              <div className="ml-1 min-w-0 flex-1">
                <p className="text-[14px] sm:text-[15px] font-bold text-gray-700 m-0 leading-tight truncate">
                  {pageTitle}
                </p>
                <p className="text-[10px] sm:text-[11px] text-gray-400 m-0 hidden sm:block">
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    month:   "short",
                    day:     "numeric",
                  })}
                </p>
              </div>

              
              <Popover className="relative flex-shrink-0">
                <Popover.Button
                  as="div"
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-[6px] rounded-full cursor-pointer select-none
                    shadow-[0_2px_12px_rgba(47,109,180,0.25)]
                    hover:shadow-[0_4px_20px_rgba(47,109,180,0.38)] transition-shadow duration-200"
                  style={{ background: `linear-gradient(to right, ${THEME.primary}, ${THEME.primaryLight})` }}
                >
                  <AccountCircleIcon className="!text-[20px] !text-white/90" />
                  <span className="text-[12px] sm:text-[13px] font-semibold text-white max-w-[80px] sm:max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap hidden xs:inline">
                    {loggedUser.name || "Admin"}
                  </span>
                  <IoIosArrowDown
                    className="w-3 h-3 text-white/70 transition-transform duration-300"
                    style={{ transform: isPopoverOpen ? "rotate(-180deg)" : "rotate(0deg)" }}
                  />
                </Popover.Button>

                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 -translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-2"
                >
                  <Popover.Panel
                    className="absolute top-[calc(100%+10px)] right-0 z-[9999]
                      min-w-[180px] sm:min-w-[220px] rounded-2xl overflow-hidden
                      bg-white/[0.97] backdrop-blur-xl
                      border border-gray-700/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                  >
                    
                    <div
                      className="flex items-center gap-2.5 p-3 sm:p-4"
                      style={{ background: `linear-gradient(to right, ${THEME.primary}, ${THEME.primaryLight})` }}
                    >
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <FaRegUser className="text-white text-sm sm:text-base" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-[12px] sm:text-[13px] font-bold m-0 truncate">
                          {loggedUser.name || "Admin"}
                        </p>
                        <p className="text-white/60 text-[10px] sm:text-[11px] m-0">Receptionist</p>
                      </div>
                    </div>

                   
                    <div
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 m-2 sm:m-2.5 px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-xl cursor-pointer
                        bg-gradient-to-r from-red-500 to-red-600
                        text-white font-semibold text-[12px] sm:text-[13px]
                        shadow-[0_3px_12px_rgba(239,68,68,0.3)]
                        hover:-translate-y-px hover:shadow-[0_6px_18px_rgba(239,68,68,0.4)]
                        transition-all duration-200"
                    >
                      <PowerSettingsNewIcon className="!text-[16px] sm:!text-[18px]" />
                      <span>Sign Out</span>
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>

            </div>
          </div>
        </div>

        
        <div className="flex-1 p-2 pt-2">
          <div className="bg-white min-h-[calc(100vh-80px)] p-3 sm:p-4 rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}